/* eslint-disable react/prop-types */
/* eslint-disable camelcase */
import React, {
  useEffect, useCallback, useRef, useState,
} from 'react';
import { PropTypes } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
  Table, Select, Modal, Spin, Button, Form,
} from 'antd';
import { debounce } from 'lodash';
import { PlusOutlined } from '@ant-design/icons';

import { MerchantUserColumns } from './Columns';
import { getAllUsers } from '../../redux/actions/Auth';
import { updateMerchantAccount } from '../../redux/actions/Data';
import Can from '../../util/can';
import { USERS } from '../../constants/resources';

const { Option } = Select;
const formItemLayout = {
  wrapperCol: { span: 16 },
};

const MerchantUsers = ({ merchant }) => {
  const dispatch = useDispatch();
  const [visible, setVisible] = useState(false);
  const [selectedUsers, setselectedUsers] = useState([]);
  const { data: userData, loadingUsers } = useSelector(({ auth }) => auth.users);
  const { loadingAuthUser, authUser } = useSelector(({ auth }) => auth);
  const loading = loadingUsers || loadingAuthUser;
  const isMerchant = authUser.role.name.includes('Merchant');

  const form = useRef();

  useEffect(() => {
    dispatch(getAllUsers('', isMerchant));
  }, [dispatch]);

  const userSearch = (search) => {
    const searchFilter = JSON.stringify([
      { full_name: { $regex: search.trim(), $options: 'i' } },
      { first_name: { $regex: search.trim(), $options: 'i' } },
      { last_name: { $regex: search.trim(), $options: 'i' } },
      { email: { $regex: search.trim(), $options: 'i' } },
    ]);
    const searchParams = `filter={"$or": ${searchFilter}}`;
    dispatch(getAllUsers(searchParams, isMerchant));
  };

  const debouncedSearch = useCallback(
    debounce((nextValue) => userSearch(nextValue), 1000),
    [],
  );

  const handleUserSearch = (value) => {
    debouncedSearch(value);
  };

  const removeUser = (record) => {
    const new_merchant_users = merchant.merchant_users.filter((user) => user._id !== record._id);
    dispatch(
      updateMerchantAccount(merchant._id, { ...merchant, merchant_users: new_merchant_users }),
    );
  };

  const handleShowModal = () => {
    if (form.current) {
      form.current.resetFields();
    }
    // Set initial selected users to existing merchant users' IDs
    const existingUserIds = merchant.merchant_users?.map(user => user._id) || [];
    setselectedUsers(existingUserIds);
    setVisible(true);
  };

  const handleOk = () => {
    if (form.current) {
      form.current.validateFields().then((values) => {
        // Merge existing merchant users with newly selected users
        const existingUsers = merchant.merchant_users || [];
        const newUserIds = values.users || [];

        // Create a map of existing users by ID for quick lookup
        const existingUsersMap = new Map(
          existingUsers.map(user => [user._id, user])
        );

        // Build the final user list
        const finalUsers = newUserIds.map((userId) => {
          // Check if user already exists in merchant_users
          if (existingUsersMap.has(userId)) {
            return existingUsersMap.get(userId);
          }

          // Otherwise, find the user in userData
          const userObj = userData.find(element => element._id === userId);

          if (userObj) {
            const { email, _id, full_name } = userObj;
            return { full_name, email, _id };
          }

          // Skip if user not found (shouldn't happen)
          return null;
        }).filter(user => user !== null); // Remove any null entries

        dispatch(
          updateMerchantAccount(merchant._id, {
            ...merchant,
            merchant_users: finalUsers,
          }),
        );
        setTimeout(() => {
          setVisible(false);
          setselectedUsers([]);
        }, 2000);
      }).catch((error) => {
        console.error('Form validation failed:', error);
      });
    }
  };

  const handleCancel = () => {
    setVisible(false);
    setselectedUsers([]);
  };

  const usersOptions = userData && userData
    .map((user) => (<Option key={user._id}>{`${user.full_name} - ${user.email}`}</Option>));

  const tableProps = {
    pagination: false,
    columns: MerchantUserColumns(removeUser),
    dataSource: merchant.merchant_users,
  };

  return (
    (
      <div>
        <div className="gx-d-flex justify-content-center">
           <Can I="create" a={USERS}>
            <Button
              icon={<PlusOutlined />}
              type="primary"
              onClick={() => handleShowModal()}
            >
              Add Merchant User
            </Button>
           </Can>
        </div>
        <div>
          <Table className="gx-table-responsive" {...tableProps} />
        </div>
        <Modal
          title="Add merchant user"
          open={visible}
          okText="Add"
          onOk={handleOk}
          onCancel={handleCancel}
          centered
          width={600}
        >
          <Form
            name="merchant-form"
            className="gx-form-row0"
            ref={form}
            {...formItemLayout}
          >
            <Form.Item
              name="users"
              label="Merchant User"
              initialValue={selectedUsers}
              rules={[
                { required: true, message: 'This input is required!' },
              ]}
            >
              <Select
                mode="multiple"
                showSearch
                value={selectedUsers}
                placeholder="Search for a user by email"
                showArrow={false}
                filterOption={false}
                onSearch={handleUserSearch}
                onChange={(value) => setselectedUsers(value)}
                notFoundContent={loading ? <Spin size="small" /> : 'No User found'}
              >
                {usersOptions}
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    )
  );
};

MerchantUsers.propTypes = {
  merchantUsers: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
  ),
};

MerchantUsers.defaultProps = {
  merchantUsers: [],
};

export default MerchantUsers;
